import { createAction } from "@reduxjs/toolkit";
import api from "../../services/api";

/**
 * @typedef {import("./types").Rdm} Rdm
 * @typedef {import("./types").RdmUser} RdmUser
 * @typedef {import("./types").RdmCreateData} RdmCreateData
 * @typedef {import("./types").RdmUpdateData} RdmUpdateData
 * @typedef {import("./types").RdmUserCreateBasicData} RdmUserCreateBasicData
 * @typedef {import("./types").RdmUserUpdateData} RdmUserUpdateData
 * @typedef {import("./types").RdmQuery} RdmQuery
 */

export const paginateRdmsRequest = createAction("PAGINATE_RDMS_REQUEST");
export const paginateRdmsSuccess = createAction("PAGINATE_RDMS_SUCCESS");
export const paginateRdmsFailure = createAction("PAGINATE_RDMS_FAILURE");

export const addRdm = createAction("ADD_RDM");
export const removeRdm = createAction("REMOVE_RDM");

/**
 * @param {Object} props
 * @param {number} props.page
 * @param {number} props.limit
 * @param {number} [props.sortOrder=-1]
 * @param {string} [props.title=""]
 * @returns {Promise<void>}
 */
export const handlePaginate =
  ({ page, limit, sortOrder, title }) =>
  async (dispatch) => {
    dispatch(paginateRdmsRequest());
    try {
      const { data } = await api.get(`/rdm/paginate/`, {
        params: { page, limit, sortOrder, title },
      });
      dispatch(paginateRdmsSuccess(data));
    } catch (error) {
      dispatch(paginateRdmsFailure(error));
    }
  };

/**
 * @param {string} id
 * @returns {Promise<Rdm>}
 */
export const handleGetRdm = async (id) => {
  return api
    .get(`/rdm/${id}`)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {RdmCreateData} rdmData
 * @returns {Promise<Rdm>}
 */
export const handleAddRdm = async (rdmData) => {
  return api
    .post("/rdm", rdmData)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {string} id
 * @param {RdmUserCreateBasicData} rdmUserData
 * @returns {Promise<RdmUser>}
 */
export const handleAddRdmUser = async (id, rdmUserData) => {
  return api
    .post(`/rdm/${id}/user`, rdmUserData)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {string} id
 * @param {RdmUpdateData} rdmData
 * @returns {Promise<Rdm>}
 */
export const handleUpdateRdm = async (id, rdmData) => {
  return api
    .patch(`/rdm/${id}`, rdmData)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {string} rdmUserId
 * @param {RdmUserUpdateData} rdmUserData
 * @returns {Promise<RdmUser>}
 */
export const handleUpdateRdmUser = async (rdmUserId, rdmUserData) => {
  return api
    .patch(`/rdm/${rdmUserId}/user`, rdmUserData)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {string} id
 * @returns {Promise<any>}
 */
export const handleRemoveRdm = async (id) => {
  return api
    .delete(`/rdm/${id}`)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};

/**
 * @param {string} rdmUserid
 * @returns {Promise<any>}
 */
export const handleRemoveRdmUser = async (rdmUserId) => {
  return api
    .delete(`/rdm/${rdmUserId}/user`)
    .then((res) => res.data)
    .catch((err) => {
      throw err.response;
    });
};
